<template>
  <ManagerLayout fixed-list>
    <header class="header">
      <div class="header-inner">
        <strong>启明服务管理平台</strong>
        <nav aria-label="主导航">
          <router-link to="/">首页</router-link>
          <router-link to="/wechat-followers">微信用户</router-link>
          <router-link class="active" to="/feedback">患者反馈</router-link>
        </nav>
        <div class="account">
          <span>{{ employee.displayName || employee.username }}</span>
          <button type="button" @click="handleLogout">退出登录</button>
        </div>
      </div>
    </header>

    <main class="main manager-list-page">
      <div class="heading">
        <div>
          <h1>患者反馈</h1>
          <p>共 {{ total }} 条</p>
        </div>
      </div>

      <form class="search" @submit.prevent="search">
        <label class="sr-only" for="feedback-search">搜索意见</label>
        <input id="feedback-search" v-model="searchText" type="search" placeholder="搜索 OpenID、内容、称呼或手机号">
        <label class="date-filter"><span>提交日期</span><input v-model="searchStartDate" type="date" aria-label="提交开始日期"></label>
        <span class="date-separator">至</span>
        <label class="date-filter"><span class="sr-only">提交结束日期</span><input v-model="searchEndDate" type="date" aria-label="提交结束日期"></label>
        <ManagerQueryButton :disabled="loading" />
        <ManagerResetButton :disabled="loading" @click="resetSearch" />
      </form>

      <div class="table-wrap" aria-live="polite">
        <ManagerTable :items="items" :loading="loading" :error="error" :empty-text="keyword || appliedStartDate || appliedEndDate ? '没有找到匹配的意见' : '暂无用户意见'" @retry="loadFeedback">
          <el-table-column prop="content" label="反馈内容" min-width="280" show-overflow-tooltip />
          <el-table-column label="反馈类型" min-width="120"><template slot-scope="{ row }">{{ row.feedbackType || '未填写' }}</template></el-table-column>
          <el-table-column label="联系人" min-width="110"><template slot-scope="{ row }">{{ row.contactName || '未填写' }}</template></el-table-column>
          <el-table-column label="OpenID" min-width="240"><template slot-scope="{ row }"><span class="openid-cell">{{ row.openid }}</span></template></el-table-column>
          <el-table-column label="提交时间" min-width="160"><template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template></el-table-column>
          <el-table-column label="操作" width="110" fixed="right"><template slot-scope="{ row }"><button type="button" class="detail-button" @click="selectedFeedback = row">查看详情</button></template></el-table-column>
        </ManagerTable>
      </div>

      <ManagerPagination :page="page" :pages="pages" :total="total" :disabled="loading" @change="changePage" />
    </main>

    <RecordDetails v-if="selectedFeedback" title="患者反馈详情" @close="selectedFeedback = null">
      <section class="detail-section">
        <h3>反馈内容</h3>
        <p class="detail-content">{{ selectedFeedback.content }}</p>
      </section>
      <dl class="detail-fields">
        <div><dt>反馈类型</dt><dd>{{ selectedFeedback.feedbackType || '未填写' }}</dd></div>
        <div><dt>服务评分</dt><dd>{{ ratingText(selectedFeedback.serviceRating) }}</dd></div>
        <div><dt>联系人</dt><dd>{{ selectedFeedback.contactName || '未填写' }}</dd></div>
        <div><dt>手机号</dt><dd>{{ selectedFeedback.phoneNumber || '未填写' }}</dd></div>
        <div><dt>就诊日期</dt><dd>{{ selectedFeedback.visitDate || '未填写' }}</dd></div>
        <div><dt>提交时间</dt><dd>{{ formatTime(selectedFeedback.createdAt) }}</dd></div>
        <div class="detail-openid"><dt>OpenID</dt><dd>{{ selectedFeedback.openid }}</dd></div>
      </dl>
    </RecordDetails>
  </ManagerLayout>
</template>

<script>
import { isNavigationFailure } from 'vue-router'
import { API_BASE } from '@/config/api'
import { state, clear, logout } from '@/store/session'
import ManagerLayout from '@/components/ManagerLayout.vue'
import RecordDetails from '@/components/RecordDetails.vue'
import ManagerTable from '@/components/ManagerTable.vue'
import ManagerQueryButton from '@/components/ManagerQueryButton.vue'
import ManagerResetButton from '@/components/ManagerResetButton.vue'
import ManagerPagination from '@/components/ManagerPagination.vue'
import TableColumn from 'element-ui/lib/table-column'

export default {
  name: 'FeedbackInbox',
  components: { ManagerLayout, RecordDetails, ManagerTable, ManagerQueryButton, ManagerResetButton, ManagerPagination, 'el-table-column': TableColumn },
  data () {
    return {
        items: [], searchText: '', keyword: '', searchStartDate: '', searchEndDate: '', appliedStartDate: '', appliedEndDate: '', page: 0,
      total: 0, pages: 0, loading: false, error: '', redirectingToLogin: false,
      selectedFeedback: null
    }
  },
  computed: {
    employee () { return state.employee || {} }
  },
  created () { this.loadFeedback() },
  methods: {
    async handleLogout () {
      await logout()
      if (this.$route.name === 'login') return
      try {
        await this.$router.replace({ name: 'login' })
      } catch (error) {
        if (!isNavigationFailure(error)) window.location.hash = '#/login'
      }
    },
    async handleUnauthorized () {
      if (this.$route.name !== 'feedback' || this.redirectingToLogin) return
      this.redirectingToLogin = true
      const redirect = this.$route.fullPath
      clear()
      try {
        await this.$router.replace({ name: 'login', query: { redirect } })
      } catch (error) {
        if (!isNavigationFailure(error)) this.error = '登录状态已失效，请刷新页面'
      }
    },
    async loadFeedback () {
      this.loading = true
      this.error = ''
      this.selectedFeedback = null
      try {
        const params = new URLSearchParams({ keyword: this.keyword, page: String(this.page), size: '20' })
        if (this.appliedStartDate) params.set('startDate', this.appliedStartDate)
        if (this.appliedEndDate) params.set('endDate', this.appliedEndDate)
        const response = await fetch(`${API_BASE}/api/manager/feedback?${params}`, { credentials: 'include' })
        const result = await response.json()
        if (result.code === 401) {
          await this.handleUnauthorized()
          return
        }
        if (!response.ok || result.code !== 0) throw new Error(result.msg || '加载失败')
        this.items = result.data.content
        this.total = result.data.totalElements
        this.pages = result.data.totalPages
      } catch (error) {
        this.items = []
        this.error = error.message || '加载失败，请检查服务连接'
      } finally {
        this.loading = false
      }
    },
    search () {
      if (this.searchStartDate && this.searchEndDate && this.searchEndDate < this.searchStartDate) {
        this.error = '结束日期不能早于开始日期'
        return
      }
      this.keyword = this.searchText.trim()
      this.appliedStartDate = this.searchStartDate
      this.appliedEndDate = this.searchEndDate
      this.page = 0
      this.loadFeedback()
    },
    resetSearch () {
      this.searchText = ''
      this.keyword = ''
      this.searchStartDate = ''
      this.searchEndDate = ''
      this.appliedStartDate = ''
      this.appliedEndDate = ''
      this.page = 0
      this.loadFeedback()
    },
    changePage (page) {
      this.page = page
      this.loadFeedback()
    },
    formatTime (value) { return value ? value.replace('T', ' ').slice(0, 16) : '' },
    ratingText (value) { return value ? `${value} 分` : '暂不评分' }
  }
}
</script>

<style scoped>
.header { display: none; background: #fff; border-bottom: 1px solid #e2e8e6; }
.header-inner { display: flex; align-items: center; gap: 40px; max-width: 1200px; min-height: 64px; padding: 0 28px; margin: auto; }
.header strong { color: #173b46; font-size: 16px; white-space: nowrap; }
.header nav { display: flex; align-items: center; gap: 26px; min-height: 64px; }
.header a { display: flex; align-items: center; min-height: 64px; color: #5c7376; font-size: 14px; text-decoration: none; white-space: nowrap; }
.header a.active { border-bottom: 2px solid #268f81; color: #237d72; font-weight: 600; }
.account { display: flex; align-items: center; gap: 18px; margin-left: auto; color: #52696c; font-size: 13px; white-space: nowrap; }
.account button { border: 0; background: transparent; color: #237d72; cursor: pointer; font: inherit; }
.main { display: flex; flex: 1; flex-direction: column; min-width: 0; width: 100%; padding: 30px 32px 32px; }
.heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 25px; }
h1 { margin: 0; color: #18333a; font-size: 26px; }
.heading p { margin: 8px 0 0; color: #819294; font-size: 13px; }
.search { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin-bottom: 20px; }
.search > input[type="search"] { flex: 1 1 260px; width: auto; }
.search input { width: min(100%, 420px); height: 42px; padding: 0 13px; border: 1px solid #d5e0dd; border-radius: 5px; background: #fff; color: #18333a; font: inherit; font-size: 13px; }
.date-filter { display: inline-flex; align-items: center; gap: 7px; color: #718789; font-size: 12px; white-space: nowrap; }
.date-filter input { width: 145px; padding: 0 9px; }
.date-separator { color: #819294; font-size: 12px; }
.table-wrap { flex: 1; min-width: 0; min-height: 0; overflow: auto; border: 1px solid #e2e8e6; border-radius: 6px; background: #fff; }
table { width: 100%; border-collapse: collapse; table-layout: fixed; }
th { height: 46px; padding: 0 18px; color: #728789; background: #f9fbfa; font-size: 12px; font-weight: 600; text-align: left; }
th:nth-child(1) { width: 29%; }
th:nth-child(2) { width: 12%; }
th:nth-child(3) { width: 14%; }
th:nth-child(4) { width: 22%; }
th:nth-child(5) { width: 15%; }
th:nth-child(6) { width: 8%; }
td { height: 62px; padding: 10px 18px; border-top: 1px solid #edf1ef; color: #355056; font-size: 13px; }
.content-cell, .openid-cell { min-width: 0; }
.content-cell span, .openid-cell span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.content-cell { color: #183b43; }
.openid-cell { font-family: Consolas, 'Microsoft YaHei', sans-serif; }
.time-cell { white-space: nowrap; }
.detail-button { padding: 6px 0; border: 0; color: #237f78; background: transparent; cursor: pointer; font: inherit; white-space: nowrap; }
.detail-button:hover { color: #155d55; text-decoration: underline; }
.state { height: 240px; padding: 30px 20px; color: #819294; font-size: 13px; text-align: center; }
.state.error { color: #ae5a52; }
.state button { border: 0; background: transparent; color: #237d72; cursor: pointer; }
.detail-section { padding-bottom: 26px; border-bottom: 1px solid #e5ece9; }
.detail-section h3 { margin: 0 0 14px; color: #6f8687; font-size: 12px; font-weight: 600; }
.detail-content { margin: 0; color: #183b43; font-size: 15px; line-height: 1.8; white-space: pre-wrap; overflow-wrap: anywhere; }
.detail-fields { margin: 8px 0 0; }
.detail-fields div { display: grid; grid-template-columns: 88px minmax(0, 1fr); gap: 16px; padding: 17px 0; border-bottom: 1px solid #edf1ef; font-size: 13px; }
.detail-fields dt { color: #819294; }
.detail-fields dd { min-width: 0; margin: 0; color: #355056; overflow-wrap: anywhere; }
.detail-openid dd { font-family: Consolas, 'Microsoft YaHei', sans-serif; }
.pagination { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-top: 18px; color: #819294; font-size: 12px; }
.pagination span { margin-right: 8px; }
.pagination button { height: 34px; padding: 0 12px; border: 1px solid #dce5e2; border-radius: 5px; background: #fff; color: #385a5e; cursor: pointer; }
button:disabled { opacity: .5; cursor: default; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
@media (max-width: 900px) {
  .header { display: block; }
}
@media (min-width: 701px) and (max-width: 1200px) {
  th, td { padding-right: 10px; padding-left: 10px; }
  th:nth-child(1) { width: 26%; }
  th:nth-child(2) { width: 13%; }
  th:nth-child(3) { width: 13%; }
  th:nth-child(4) { width: 16%; }
  th:nth-child(5) { width: 20%; }
  th:nth-child(6) { width: 12%; }
}
@media (max-width: 700px) {
  .header-inner { flex-wrap: wrap; gap: 0 16px; padding: 12px 16px 0; }
  .header strong { width: 100%; }
  .header nav, .header a { min-height: 44px; }
  .header nav { gap: 15px; overflow-x: auto; }
  .header a { font-size: 12px; }
  .account { margin-left: auto; font-size: 12px; }
  .account span { display: none; }
  .main { padding: 28px 16px 56px; }
  h1 { font-size: 22px; }
  .search { flex-wrap: wrap; }
  .search input { width: 100%; }
  .table-wrap { min-height: 0; }
  table, tbody, tr, td { display: block; width: 100%; }
  thead { display: none; }
  tr { padding: 13px 16px; border-top: 1px solid #edf1ef; }
  tr:first-child { border-top: 0; }
  td { display: flex; gap: 12px; min-height: 30px; height: auto; padding: 5px 0; border: 0; font-size: 12px; }
  td::before { flex: 0 0 70px; color: #829597; content: attr(data-label); }
  .content-cell span { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; white-space: normal; overflow-wrap: anywhere; }
  .openid-cell span { white-space: normal; overflow-wrap: anywhere; }
  .state { display: grid; min-height: 180px; place-items: center; text-align: center; }
  .state::before { display: none; }
  .detail-fields div { grid-template-columns: 74px minmax(0, 1fr); }
}
.main { height:calc(100vh - 64px); min-height:0; overflow:hidden; }
.table-wrap { min-width:0; overflow:auto; border:0; border-radius:0; background:transparent; }
</style>
